// Pure helpers for the Usage Analytics pages — flags, filtering, and the plain-
// language findings on a user's report. Kept out of the component files so
// they only export components (react-refresh) and so the rules are testable.

import { activeNavId, navById } from '../../components/layout/navigation';

// Active in 20+ distinct clock hours = round-the-clock, which people aren't.
export const ROUND_THE_CLOCK_HOURS = 20;

export function flagsFor(row, limits) {
  const flags = [];
  const near = limits?.near_limit_pct ?? 80;
  if (row.throttled > 0 || row.rate_limited_minutes > 0) flags.push({ key: 'limited', label: 'Rate-limited', tone: 'danger' });
  else if (row.peak_pct_of_limit != null && row.peak_pct_of_limit >= near) flags.push({ key: 'near', label: 'Near limit', tone: 'warning' });
  if (row.script_calls > 0) flags.push({ key: 'script', label: 'Script', tone: 'info' });
  if (row.active_hours_of_day >= ROUND_THE_CLOCK_HOURS) flags.push({ key: 'clock', label: '24/7', tone: 'warning' });
  return flags;
}

export function filterAndSort(users, { audience, query, minCalls, flaggedOnly, sort, dir }, limits) {
  const q = query.trim().toLowerCase();
  const out = users.filter((u) => {
    if (audience !== 'all' && audienceOf(u) !== audience) return false;
    if (minCalls && u.calls < minCalls) return false;
    if (flaggedOnly && flagsFor(u, limits).length === 0) return false;
    if (q && ![u.name, u.email, u.store_name, String(u.user_id)].some((v) => v && String(v).toLowerCase().includes(q))) return false;
    return true;
  });
  const sign = dir === 'asc' ? 1 : -1;
  return out.sort((a, b) => {
    const av = a[sort] ?? '';
    const bv = b[sort] ?? '';
    return (av > bv ? 1 : av < bv ? -1 : 0) * sign;
  });
}

export function median(values) {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const pct = (part, whole) => (whole ? Math.round((part * 100) / whole) : 0);

/**
 * Overall read of a user's report: 'scraping' | 'watch' | 'normal'.
 * Deliberately conservative — it points a human at the evidence, it does not
 * accuse anyone; our own integrations will read 'watch'.
 */
export function verdictFor(summary, limits) {
  const s = summary || {};
  const near = limits?.near_limit_pct ?? 80;
  const scriptShare = pct(s.script_calls, s.calls);
  const roundClock = s.active_hours_of_day >= ROUND_THE_CLOCK_HOURS;
  const nearLimit = (s.peak_pct_of_limit ?? 0) >= near || s.throttled > 0;

  if ((nearLimit && (scriptShare >= 50 || roundClock)) || (scriptShare >= 50 && roundClock && s.records_viewed > 500)) {
    return { key: 'scraping', label: 'Likely scraping', tone: 'danger', reason: 'Pressing against the rate limit with scripted or round-the-clock traffic. Check the endpoints and records below.' };
  }
  if (nearLimit || scriptShare >= 50 || roundClock) {
    return { key: 'watch', label: 'Worth a look', tone: 'warning', reason: nearLimit ? 'Close to or over the rate limit at least once.' : scriptShare >= 50 ? 'Mostly scripted traffic. Expected for our own integrations; unusual for a person.' : 'Active around the clock, which people rarely sustain.' };
  }
  return { key: 'normal', label: 'Normal use', tone: 'success', reason: 'Browser traffic, well under the rate limit, during normal hours.' };
}

/**
 * Concise findings, grouped, each one sentence. `peers` = medians from the
 * list report ({ team, customers } calls per active day), optional.
 */
export function insightsFor(detail, peers) {
  const s = detail?.summary || detail?.fallbackSummary || {};
  const limits = detail?.limits || {};
  const days = detail?.days || 7;
  const out = { usage: [], limits: [], automation: [], pattern: [], breadth: [] };
  if (!s.calls) return out;

  const perDay = s.active_days ? Math.round(s.calls / s.active_days) : 0;
  out.usage.push(`${n(s.calls)} API calls in ${days === 1 ? '24 hours' : `${days} days`}, active on ${s.active_days} of ${days} day${days === 1 ? '' : 's'}.`);
  out.usage.push(`${n(perDay)} calls per active day.`);
  const group = audienceOf(detail?.user);
  const peer = peers?.[group];
  if (peer) {
    const ratio = perDay / peer;
    const who = { team: 'the RC team', merchant: 'merchants', client: 'clients' }[group];
    out.usage.push(ratio >= 1.2 || ratio <= 0.8
      ? `${ratio.toFixed(1)}× the median for ${who} (${n(Math.round(peer))}/day).`
      : `In line with the median for ${who} (${n(Math.round(peer))}/day).`);
  }
  if (s.write_calls) out.usage.push(`${pct(s.write_calls, s.calls)}% of calls change data (create, update, delete); the rest are reads.`);

  const limit = limits.api_per_minute_user;
  if (limit) {
    out.limits.push(`Busiest minute: ${n(s.peak_per_minute)} requests, ${s.peak_pct_of_limit ?? 0}% of the ${n(limit)}/min limit.`);
  }
  out.limits.push(s.throttled
    ? `Blocked by the rate limiter ${n(s.throttled)} time${s.throttled === 1 ? '' : 's'}.`
    : 'Never blocked by the rate limiter.');
  if (s.client_errors || s.server_errors) {
    out.limits.push(`${n(s.client_errors)} refused requests (4xx) and ${n(s.server_errors)} server errors (5xx).`);
  }

  const scriptShare = pct(s.script_calls, s.calls);
  const topScript = (detail.user_agents || []).find((u) => u.is_script);
  out.automation.push(s.script_calls
    ? `${scriptShare}% of calls come from a script or bot${topScript?.user_agent ? ` (${topScript.user_agent.split(' ')[0]})` : ''}.`
    : 'All calls come from a regular browser or the app.');
  if (!s.script_calls) {
    out.automation.push('A browser-use AI agent would also appear as a browser here, so read the pattern below too.');
  }

  const hours = (detail.hour_of_day || []).filter((h) => h.calls > 0);
  if (hours.length) {
    const top = [...hours].sort((a, b) => b.calls - a.calls)[0];
    out.pattern.push(`Active in ${s.active_hours_of_day} of 24 hours of the day; busiest at ${String(top.hour).padStart(2, '0')}:00 UTC.`);
  }
  if (s.active_hours_of_day >= ROUND_THE_CLOCK_HOURS) out.pattern.push('Round-the-clock activity, which people rarely sustain.');
  if (s.avg_ms != null) out.pattern.push(`Average response ${n(s.avg_ms)} ms; ${n(s.server_seconds)} s of server time in total.`);

  const features = detail.features || [];
  if (features.length) {
    const total = features.reduce((a, f) => a + f.calls, 0);
    out.breadth.push(`Uses ${s.features_used} feature${s.features_used === 1 ? '' : 's'}; top: ${features[0].feature} (${pct(features[0].calls, total)}% of calls).`);
  }
  if (s.records_viewed) {
    const types = (detail.records_by_type || []).slice(0, 3).map((r) => `${n(r.records)} ${r.type.toLowerCase()}${r.records === 1 ? '' : 's'}`);
    out.breadth.push(`Opened ${n(s.records_viewed)} distinct records${types.length ? `: ${types.join(', ')}` : ''}.`);
  }
  return out;
}

export const WINDOWS = [
  { value: '1', label: 'Today' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
];

export function fmtSeconds(s) {
  if (!s) return '0 s';
  if (s < 120) return `${s} s`;
  if (s < 7200) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}

/** API timestamps are UTC "YYYY-MM-DD HH:MM:SS" (or ISO); shown in local time. */
export function fmtWhen(value) {
  if (!value) return '—';
  const raw = String(value);
  const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-GB', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Every day in the window, zero-filled, so gaps read as gaps. */
export function fillDays(daily, days, now = new Date()) {
  const byDay = Object.fromEntries((daily || []).map((d) => [d.day, d.calls]));
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key, label: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' }), calls: byDay[key] || 0 });
  }
  return out;
}

/** "just now" / "40 s ago" / "12 min ago" / "3 h ago" / date — for UTC API timestamps. */
export function timeAgo(value, now = Date.now()) {
  if (!value) return '—';
  const raw = String(value);
  const t = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z').getTime();
  if (Number.isNaN(t)) return raw;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return fmtWhen(value);
}

/** Short device label from a user agent: "iPhone", "Android", "Mac", "Windows", "RC app", "Script". */
export function deviceFrom(userAgent, isScript = false) {
  const ua = String(userAgent || '');
  if (isScript) return 'Script';
  if (/CFNetwork|Darwin|Expo|okhttp/i.test(ua)) return 'RC app';
  if (/iPhone|iPad/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  return ua ? 'Browser' : 'Unknown';
}

/** api segment -> label: "trade-feed" -> "Trade feed". */
export function featureLabel(feature) {
  if (!feature) return '—';
  const s = String(feature).replace(/[-_]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * One "unusual activity" marker for a user row, or null when nothing stands
 * out. Danger when the rate limit is involved, warning for scripts / 24/7.
 */
export function unusualFor(row, limits) {
  const flags = flagsFor(row, limits);
  if (!flags.length) return null;
  const danger = flags.some((f) => f.key === 'limited' || f.key === 'near');
  return {
    tone: danger ? 'danger' : 'warning',
    label: `Unusual activity: ${flags.map((f) => f.label).join(', ')}`,
  };
}

/**
 * Friendly name for a frontend path the app reported ("/rc/listings/3642" ->
 * "Listings"), from the sidebar's own route table; falls back to the path.
 */
export function pageLabel(path) {
  if (!path) return null;
  const id = activeNavId(path);
  return (id && navById(id)?.label) || path;
}

/**
 * Which audience tab a user belongs to: "team" (RC staff), "merchant" (a
 * business contact) or "client". The backend decides from users.type; older
 * payloads without it fall back to the team flag.
 */
export function audienceOf(user) {
  if (!user) return 'client';
  if (user.audience) return user.audience;
  return user.is_team ? 'team' : 'client';
}

/** "RC team", or the user type in words: "Client", "Merchant", "Personal shopper". */
export function typeLabel(user) {
  if (!user) return null;
  if (user.is_team || user.audience === 'team') return 'RC team';
  const t = user.user_type || (user.audience === 'merchant' ? 'merchant' : 'client');
  const words = String(t).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A store_user role in the words Alex uses: "Store Owner", "Owner Admin", "Sales Admin". */
export const ROLE_LABELS = { owner: 'Store Owner', store_admin: 'Owner Admin', sales_admin: 'Sales Admin' };

export function roleLabel(role) {
  return ROLE_LABELS[role] || (role ? String(role).replace(/_/g, ' ') : '');
}
